import { Fragment, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Key, Plus, X, Trash2, Copy, Check, AlertCircle, Code, Webhook, BookOpen, Eye, ScrollText, RefreshCw, ChevronLeft, ChevronRight, ChevronDown, Search } from 'lucide-react';
import { api, API_BASE_URL } from '@/api/client';
import { WebhookManager } from '@/features/webhooks/WebhookManager';

export function MerchantApiPage() {
  const [tab, setTab] = useState<'keys' | 'webhooks' | 'logs' | 'docs'>('keys');
  return (
    <div className="p-6 max-w-[1300px] mx-auto">
      <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2 mb-1">
        <Code size={22} className="text-emerald-700" /> Developer API
      </h1>
      <p className="text-sm text-slate-500 mb-4">
        API keys for custom integrations. Connecting a <b>Shopify</b> store? Use{' '}
        <Link to="/channels" className="text-emerald-700 font-medium hover:underline">Sales Channels</Link> instead.
      </p>
      <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1 mb-4 w-fit">
        {[
          { id: 'keys' as const, label: 'API Keys', Icon: Key },
          { id: 'webhooks' as const, label: 'Webhooks', Icon: Webhook },
          { id: 'logs' as const, label: 'API Logs', Icon: ScrollText },
          { id: 'docs' as const, label: 'API Documentation', Icon: BookOpen },
        ].map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-4 py-1.5 text-sm rounded-md transition flex items-center gap-1.5 ${tab === t.id ? 'bg-white shadow-sm text-emerald-700 font-medium' : 'text-slate-600 hover:text-slate-900'}`}>
            <t.Icon size={13} /> {t.label}
          </button>
        ))}
      </div>
      {tab === 'keys' && <KeysTab />}
      {tab === 'webhooks' && <WebhookManager />}
      {tab === 'logs' && <LogsTab />}
      {tab === 'docs' && <DocsTab />}
    </div>
  );
}

// Read-only info box: the platform endpoint a merchant POSTs orders to (auto-detects merchant from the
// API key, drops into their Orders Bucket). Shown at the top of the API Keys tab.
function OrderDropEndpoint() {
  const [copied, setCopied] = useState<string | null>(null);
  const url = `${API_BASE_URL}/merchant-api/orders/bucket`;
  const copy = (text: string, tag: string) => { navigator.clipboard.writeText(text); setCopied(tag); setTimeout(() => setCopied(null), 1500); };
  return (
    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 space-y-2">
      <div className="font-semibold text-emerald-900 flex items-center gap-2"><Webhook size={14} /> Your order drop endpoint</div>
      <p className="text-xs text-emerald-800">POST your orders here and pass your API key in the <code className="bg-emerald-100 px-1 rounded">X-Api-Key</code> header. The merchant is auto-detected from the key (do <b>not</b> send a merchant id). Orders land in your <b>Orders Bucket</b>.</p>
      <div className="flex items-center gap-2">
        <code className="flex-1 bg-slate-900 text-emerald-300 px-3 py-2 rounded text-xs break-all">POST {url}</code>
        <button onClick={() => copy(url, 'url')} className="border bg-white px-3 py-2 rounded text-xs flex items-center gap-1 whitespace-nowrap">{copied === 'url' ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}</button>
      </div>
      <code className="block bg-slate-900 text-emerald-300 px-3 py-2 rounded text-xs break-all">X-Api-Key: gnt_live_…&nbsp;&nbsp;(your API key from the table below)</code>
    </div>
  );
}

function KeysTab() {
  const qc = useQueryClient();
  const [show, setShow] = useState(false);
  const [revealedKey, setRevealedKey] = useState<{ apiKey: string; reReveal?: boolean } | null>(null);
  const { data = [] } = useQuery({ queryKey: ['merchant-api-keys'], queryFn: async () => (await api.get('/merchant/api-keys')).data });
  const revoke = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/merchant/api-keys/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['merchant-api-keys'] }),
  });
  const reveal = useMutation({
    mutationFn: async (id: number) => (await api.get(`/merchant/api-keys/${id}/reveal`)).data,
    onSuccess: (d: any) => setRevealedKey({ apiKey: d.apiKey, reReveal: true }),
    onError: (e: any) => alert(e?.response?.data?.message ?? 'Could not reveal this key.'),
  });
  return (
    <div className="space-y-4">
      <OrderDropEndpoint />
      <div className="flex justify-between items-center">
        <p className="text-sm text-slate-500">Generate API keys to integrate your storefront / OMS with the courier platform. <b>The full key is shown only once at creation</b> — store it securely.</p>
        <button onClick={() => setShow(true)} className="bg-emerald-600 text-white px-4 py-2 rounded text-sm flex items-center gap-1"><Plus size={14} /> Create API Key</button>
      </div>
      <div className="bg-white border rounded overflow-hidden">
        <table className="min-w-full text-sm">
          <thead className="bg-emerald-50"><tr>
            <th className="p-2 text-left">Name</th><th className="text-left">Key Prefix</th><th className="text-left">Scopes</th>
            <th className="text-right">Rate Limit</th><th>Status</th><th>Last Used</th><th />
          </tr></thead>
          <tbody>
            {(data as any[]).length === 0 ? <tr><td colSpan={7} className="text-center py-6 text-slate-400">No API keys yet</td></tr>
              : (data as any[]).map((k: any) => (
                <tr key={k.id} className="border-t">
                  <td className="p-2 font-medium">{k.name}</td>
                  <td className="font-mono text-xs">{k.keyPrefix}…</td>
                  <td className="text-xs">{(k.scopes ?? []).join(', ')}</td>
                  <td className="text-right text-xs">{k.rateLimitRpm}/min</td>
                  <td>{k.isActive ? <span className="text-emerald-600">●</span> : <span className="text-gray-400">●</span>}</td>
                  <td className="text-xs">{k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleDateString() : 'Never'}</td>
                  <td className="whitespace-nowrap">
                    <button onClick={() => reveal.mutate(k.id)} disabled={reveal.isPending}
                      title="Reveal & copy full key"
                      className="text-emerald-700 mr-3 inline-flex items-center gap-1 disabled:opacity-50"><Eye size={14} /></button>
                    <button onClick={() => confirm('Delete this key permanently? Any application using it will immediately stop working.') && revoke.mutate(k.id)} title="Delete key" className="text-red-600"><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))
            }
          </tbody>
        </table>
      </div>
      {show && <KeyForm onClose={() => setShow(false)} onCreated={(k: any) => { setShow(false); setRevealedKey({ apiKey: k.apiKey }); qc.invalidateQueries({ queryKey: ['merchant-api-keys'] }); }} />}
      {revealedKey && <RevealModal data={revealedKey} onClose={() => setRevealedKey(null)} />}
    </div>
  );
}

function KeyForm({ onClose, onCreated }: any) {
  const [form, setForm] = useState<any>({ name: '', environment: 'live', rateLimitRpm: 120 });
  const create = useMutation({
    mutationFn: async () => (await api.post('/merchant/api-keys', form)).data,
    onSuccess: onCreated,
  });
  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center pt-12 z-50">
      <div className="bg-white rounded-lg w-[560px]">
        <div className="bg-emerald-700 text-white px-5 py-3 flex items-center justify-between"><div className="font-semibold">Create API Key</div><button onClick={onClose}><X size={18} /></button></div>
        <div className="p-5 space-y-3">
          <div><label className="block text-xs text-gray-500 mb-1">Name *</label><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="My Shopify Store" className="w-full border rounded px-2 py-1.5 text-sm" /></div>
          <div><label className="block text-xs text-gray-500 mb-1">Environment</label><select value={form.environment} onChange={(e) => setForm({ ...form, environment: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm"><option value="live">Live</option><option value="test">Test</option></select></div>
          {/* The webhook URL used to be collected here and NOTHING ever read it — the field was stored
              on the key and no code path delivered to it, so every merchant who filled it in received
              nothing. Webhooks now live on their own tab where they can be tested and their delivery
              log inspected; collecting a second, dead copy here would recreate the exact same trap. */}
          <div className="bg-slate-50 border border-slate-200 rounded p-2.5 text-xs text-slate-600 flex items-start gap-2">
            <Webhook size={14} className="text-slate-400 mt-0.5 shrink-0" />
            <span>Want tracking pushed to your system? Set it up under the <b>Webhooks</b> tab — you can test the endpoint and see every delivery there.</span>
          </div>
          <div><label className="block text-xs text-gray-500 mb-1">Rate Limit (per minute)</label><input type="number" value={form.rateLimitRpm} onChange={(e) => setForm({ ...form, rateLimitRpm: Number(e.target.value) })} className="w-full border rounded px-2 py-1.5 text-sm" /></div>
        </div>
        <div className="px-5 py-3 border-t flex justify-end gap-3"><button onClick={onClose} className="border border-red-400 text-red-600 px-4 py-1.5 rounded text-sm">CANCEL</button><button onClick={() => create.mutate()} disabled={create.isPending || !form.name} className="bg-emerald-600 text-white px-5 py-1.5 rounded text-sm disabled:opacity-50">{create.isPending ? 'Creating…' : 'Generate'}</button></div>
      </div>
    </div>
  );
}

function RevealModal({ data, onClose }: any) {
  const [copied, setCopied] = useState('');
  const copy = (v: string, what: string) => { navigator.clipboard.writeText(v); setCopied(what); setTimeout(() => setCopied(''), 2000); };
  return (
    <div className="fixed inset-0 bg-black/60 flex items-start justify-center pt-12 z-50">
      <div className="bg-white rounded-lg w-[640px]">
        <div className="bg-emerald-700 text-white px-5 py-3 flex items-center justify-between"><div className="font-semibold flex items-center gap-2"><Key size={16} /> {data.reReveal ? 'API Key' : 'API Key Created'}</div><button onClick={onClose}><X size={18} /></button></div>
        <div className="p-5 space-y-3">
          <div className="bg-amber-50 border border-amber-300 rounded p-3 text-xs text-amber-900 flex items-start gap-2">
            <AlertCircle size={14} className="mt-0.5" />
            {data.reReveal
              ? <div><b>Keep this key secret.</b> Anyone with it can call the API as you. Copy it and store it securely.</div>
              : <div><b>Copy and store this key securely.</b> You can re-copy it later from the key list.</div>}
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">API Key</label>
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-slate-900 text-emerald-300 px-3 py-2 rounded text-xs break-all">{data.apiKey}</code>
              <button onClick={() => copy(data.apiKey, 'key')} className="border px-3 py-2 rounded text-xs flex items-center gap-1">{copied === 'key' ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}</button>
            </div>
          </div>
        </div>
        <div className="px-5 py-3 border-t flex justify-end"><button onClick={onClose} className="bg-emerald-600 text-white px-5 py-1.5 rounded text-sm">I've Copied It — Close</button></div>
      </div>
    </div>
  );
}

type ApiLog = {
  id: string; direction: 'IN' | 'OUT'; event: string | null;
  apiKeyId: number | null; keyName: string | null; method: string; path: string;
  statusCode: number; durationMs: number | null; ip: string | null;
  requestBody: string | null; responseBody: string | null;
  errorMessage: string | null; orderId: string | null; isTest: boolean; createdAt: string;
  awbNo: string | null; lrNo: string | null; invoiceNo: string | null; orderNo: string | null;
};
type ApiLogResp = {
  total: number; page: number; pageSize: number; totalPages: number;
  retentionDays: number; webhookRetentionDays?: number; searchTruncatedAt?: number; rows: ApiLog[];
};

function statusTone(code: number) {
  if (code >= 500) return 'bg-red-100 text-red-700';
  if (code >= 400) return 'bg-amber-100 text-amber-700';
  if (code >= 200 && code < 300) return 'bg-emerald-100 text-emerald-700';
  return 'bg-slate-100 text-slate-600';
}
const prettyJson = (s: string | null) => {
  if (!s) return '';
  try { return JSON.stringify(JSON.parse(s), null, 2); } catch { return s; }
};

const DIRECTIONS = [
  { id: 'ALL', label: 'All traffic' },
  { id: 'IN', label: 'Received (your calls to us)' },
  { id: 'OUT', label: 'Sent (webhooks we push to you)' },
] as const;

function LogsTab() {
  const [page, setPage] = useState(1);
  const [direction, setDirection] = useState<'ALL' | 'IN' | 'OUT'>('ALL');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const { data, isFetching, refetch } = useQuery<ApiLogResp>({
    queryKey: ['merchant-api-logs', page, direction, search],
    queryFn: async () => (await api.get('/merchant/api-logs', { params: { page, pageSize: 25, direction, search: search || undefined } })).data,
  });
  const rows = data?.rows ?? [];
  const totalPages = data?.totalPages ?? 1;
  const retention = data?.retentionDays ?? 7;
  const webhookRetention = data?.webhookRetentionDays ?? 14;

  // Submitted rather than typed-through: each keystroke would be a query against two tables, and
  // the values people search here (an AWB, an invoice number) are pasted whole anyway.
  const runSearch = (v: string) => { setSearch(v.trim()); setPage(1); };

  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm text-slate-500">
          Both directions in one timeline — requests you made with your API keys (<b>Received</b>, kept {retention} days)
          and the tracking webhooks we pushed to your endpoint (<b>Sent</b>, kept {webhookRetention} days).
          Click any row to see the exact request body and the exact response.
        </p>
        <div className="flex items-center gap-2 shrink-0">
          <select
            value={direction}
            onChange={(e) => { setDirection(e.target.value as any); setPage(1); }}
            className="border rounded px-2 py-1.5 text-sm text-slate-600"
          >
            {DIRECTIONS.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
          </select>
          <button onClick={() => refetch()} disabled={isFetching}
            className="border rounded px-3 py-1.5 text-sm flex items-center gap-1.5 text-slate-600 hover:bg-slate-50 disabled:opacity-50">
            <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') runSearch(searchInput); }}
            placeholder="Search LR No / Invoice No / AWB No / Order No…"
            className="w-full border rounded pl-8 pr-2 py-1.5 text-sm"
          />
        </div>
        <button onClick={() => runSearch(searchInput)} className="border rounded px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50">Search</button>
        {search && (
          <button onClick={() => { setSearchInput(''); runSearch(''); }} className="text-sm text-slate-500 hover:underline">Clear</button>
        )}
        {search && <span className="text-xs text-slate-500">Showing entries matching <b>{search}</b></span>}
      </div>

      {data?.searchTruncatedAt && (
        <div className="text-xs rounded border border-amber-300 bg-amber-50 text-amber-800 px-2 py-1.5">
          More than {data.searchTruncatedAt.toLocaleString()} webhook entries match “{search}” — only the most recent
          {' '}{data.searchTruncatedAt.toLocaleString()} are shown. Narrow the search to see the rest.
        </div>
      )}
      <div className="bg-white border rounded overflow-hidden">
        <table className="min-w-full text-sm">
          <thead className="bg-emerald-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="p-2 text-left">Time</th>
              <th className="text-left">Direction</th>
              <th className="text-left">Endpoint</th>
              <th className="text-left px-2">AWB No</th>
              <th className="text-left px-2">LR No</th>
              <th className="text-left px-2">Invoice No</th>
              <th className="text-left px-2">Order No</th>
              <th className="text-center">Status</th>
              <th className="text-right">Time (ms)</th>
              <th className="text-left px-2">Key / Webhook</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {isFetching && rows.length === 0 ? (
              <tr><td colSpan={11} className="text-center py-8 text-slate-400">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={11} className="text-center py-8 text-slate-400">
                {search ? `Nothing matches “${search}”.` : 'Nothing logged yet.'}
              </td></tr>
            ) : rows.map((r) => (
              <Fragment key={r.id}>
                <tr onClick={() => setOpen(open === r.id ? null : r.id)}
                  className="border-t cursor-pointer hover:bg-slate-50">
                  <td className="p-2 whitespace-nowrap text-slate-600">{new Date(r.createdAt).toLocaleString()}</td>
                  <td className="whitespace-nowrap">
                    <span className={`text-[10px] font-semibold rounded px-1.5 py-0.5 ${r.direction === 'OUT' ? 'bg-violet-100 text-violet-700' : 'bg-sky-100 text-sky-700'}`}>
                      {r.direction === 'OUT' ? '↑ Sent' : '↓ Received'}
                    </span>
                    {r.isTest && <span className="ml-1 text-[10px] rounded px-1 py-0.5 bg-slate-100 text-slate-500">test</span>}
                  </td>
                  <td className="font-mono text-xs text-slate-700 max-w-[220px] truncate" title={`${r.method} ${r.path}`}>
                    <span className="text-slate-400">{r.method}</span> {r.path}
                  </td>
                  <td className="px-2 font-mono text-xs text-slate-700">{r.awbNo ?? <span className="text-slate-300">—</span>}</td>
                  <td className="px-2 font-mono text-xs text-slate-700">{r.lrNo ?? <span className="text-slate-300">—</span>}</td>
                  {/* Amber when blank on an outbound row: a missing invoice number is the difference
                      between an ERP accepting the update and rejecting it, so it is worth spotting
                      from the list rather than being just another dash. */}
                  <td className={`px-2 font-mono text-xs ${!r.invoiceNo && r.direction === 'OUT' ? 'text-amber-600' : 'text-slate-700'}`}>
                    {r.invoiceNo ?? (r.direction === 'OUT' ? 'none' : <span className="text-slate-300">—</span>)}
                  </td>
                  <td className="px-2 font-mono text-xs text-slate-700">{r.orderNo ?? <span className="text-slate-300">—</span>}</td>
                  <td className="text-center">
                    {/* An outbound row can be a 2xx that the endpoint nonetheless rejected — the amber
                        dot is the only thing on this line that says so, so it must not be dropped. */}
                    <span className={`text-xs font-medium rounded px-1.5 py-0.5 ${r.direction === 'OUT' && r.errorMessage ? 'bg-amber-100 text-amber-700' : statusTone(r.statusCode)}`}>
                      {r.statusCode || '—'}{r.direction === 'OUT' && r.errorMessage ? ' !' : ''}
                    </span>
                  </td>
                  <td className="text-right text-xs tabular-nums text-slate-500">{r.durationMs ?? '—'}</td>
                  <td className="px-2 text-xs text-slate-600">{r.keyName ?? (r.apiKeyId ? `#${r.apiKeyId}` : '—')}</td>
                  <td className="pr-2 text-slate-400"><ChevronDown size={14} className={`transition ${open === r.id ? 'rotate-180' : ''}`} /></td>
                </tr>
                {open === r.id && (
                  <tr className="border-t bg-slate-50/60">
                    <td colSpan={11} className="p-3 space-y-2">
                      {(r.event || r.orderId || r.ip) && (
                        <div className="text-xs text-slate-500 flex flex-wrap gap-x-4">
                          {r.event && <span>Event: <code className="text-slate-700">{r.event}</code></span>}
                          {r.orderId && <span>Order: <code className="text-slate-700">#{r.orderId}</code></span>}
                          {r.ip && <span>IP: <code className="text-slate-700">{r.ip}</code></span>}
                        </div>
                      )}
                      {r.errorMessage && (
                        <div className="text-xs rounded border border-amber-300 bg-amber-50 text-amber-800 px-2 py-1.5">
                          {r.errorMessage}
                        </div>
                      )}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <div className="text-xs font-semibold text-slate-500 mb-1">
                            {r.direction === 'OUT' ? 'Request we sent' : 'Request body'}
                          </div>
                          <pre className="text-xs bg-white border rounded p-2 max-h-64 overflow-auto whitespace-pre-wrap break-all">{prettyJson(r.requestBody) || '—'}</pre>
                        </div>
                        <div>
                          <div className="text-xs font-semibold text-slate-500 mb-1">
                            {r.direction === 'OUT' ? 'Response from your endpoint' : 'Response'}
                          </div>
                          <pre className="text-xs bg-white border rounded p-2 max-h-64 overflow-auto whitespace-pre-wrap break-all">{prettyJson(r.responseBody) || '—'}</pre>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between text-sm">
        <div className="text-slate-500">{data?.total ? `Page ${page} of ${totalPages} · ${data.total} entries` : ' '}</div>
        <div className="flex items-center gap-2">
          <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1 || isFetching}
            className="px-2 py-1.5 border rounded text-slate-600 disabled:opacity-40 hover:bg-slate-50 flex items-center gap-1"><ChevronLeft size={14} /> Prev</button>
          <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages || isFetching}
            className="px-2 py-1.5 border rounded text-slate-600 disabled:opacity-40 hover:bg-slate-50 flex items-center gap-1">Next <ChevronRight size={14} /></button>
        </div>
      </div>
    </div>
  );
}

type DocField = { name: string; type: string; required?: boolean; desc: string };

/**
 * The Orders Bucket payload, field by field. Every field is OPTIONAL: an entry is a hand-off for our
 * team to review, so a missing or unrecognised value is stored as empty and never rejects the drop.
 */
const BUCKET_FIELDS: DocField[] = [
  { name: 'total_boxes', type: 'integer', desc: 'Number of boxes / packages in the consignment. Default 1.' },
  { name: 'freight_collection_type', type: 'integer', desc: 'Who pays the freight. 1 = Bill to Client (Paid — charged to your account), 2 = To-Pay (collected from the consignee on delivery). 3 (TBB) is also accepted and means the same as 1.' },
  { name: 'pickup_type', type: 'integer', desc: 'How the shipment reaches us. 1 = FM Pickup (we collect from your pickup location), 2 = Self Drop (you drop it at our hub / branch).' },
  { name: 'insurance_type', type: 'integer', desc: 'Risk cover. 1 = Owner Risk (goods travel at the owner\'s risk), 2 = Carrier Risk (carrier liability; ROV charges as per your rate card).' },
  { name: 'is_fragile', type: 'integer', desc: '1 = fragile, 0 = not fragile.' },
  { name: 'product_weight_in_kgs', type: 'number', desc: 'Total actual weight of the consignment in kg.' },
  { name: 'dimension_data[]', type: 'array', desc: 'One row per box size: qty (boxes of this size), length_in_cms, breadth_in_cms, height_in_cms.' },
  { name: 'invoice_data[]', type: 'array', desc: 'One row per invoice: invoice_no, order_no (your sales order), product_invoice_value (the VALUE in ₹ — this is where price goes), eway_bill_no.' },
  { name: 'suborders_data[]', type: 'array', desc: 'One row per sub-order / box: ident (your reference, e.g. box no.), count (QUANTITY of pieces — not a price; the value goes in invoice_data.product_invoice_value), description.' },
  { name: 'customer_name, customer_mobile, customer_alt_no, customer_email', type: 'string', desc: 'Consignee contact.' },
  { name: 'customer_address, customer_city, customer_state, customer_pincode', type: 'string', desc: 'Consignee delivery address.' },
  { name: 'channel_type', type: 'string', desc: 'Where the order came from. Default "API".' },
];

function DocsTab() {
  const base = API_BASE_URL.replace(/\/v1$/, '');
  const endpoints = [
    { method: 'POST', path: '/v1/api/v1/warehouses', desc: 'Create / register pickup warehouse', body: { name: 'Main Warehouse', contactName: 'Operations', contactMobile: '9876543210', address: 'A-12 Sector 5', city: 'Noida', state: 'Uttar Pradesh', pincode: '201301' } },
    { method: 'POST', path: '/v1/api/v1/orders/b2c', desc: 'Create a B2C order and book it with a courier. The AWB comes back in booking.awbNo', body: { orderNo: 'ORD-1001', channelOrderId: 'shopify-15832', customerName: 'Rohan', customerMobile: '9876500000', customerAddress: 'Flat 12, Brigade Road', customerCity: 'Bangalore', customerState: 'Karnataka', customerPincode: '560001', pickupPincode: '201301', codValue: 1499, invoiceValue: 1499, weight: 0.8, dimensions: { length: 25, breadth: 15, height: 5 }, products: [{ sku: 'TS-RED-M', name: 'Red T-Shirt', qty: 1, value: 1499 }] } },
    { method: 'POST', path: '/v1/api/v1/orders/b2b', desc: 'Create a B2B (multi-box) consignment and book it. The LR comes back in booking.lrNo', body: { orderNo: 'B2B-2001', customerName: 'XYZ Retail Pvt Ltd', customerMobile: '9876500000', customerAddress: 'Plot 7, Whitefield Industrial', customerCity: 'Bangalore', customerState: 'Karnataka', customerPincode: '560066', pickupPincode: '201301', invoiceValue: 250000, weight: 50, totalBoxes: 5, dimensions: { length: 100, breadth: 60, height: 60 } } },
    { method: 'POST', path: '/v1/merchant-api/orders/bucket', desc: 'Drop an order into the admin Orders Bucket (internal team decides whether to route as B2C or B2B). Useful when your channel sends mixed orders or you want a human to review before booking. Your merchant is auto-detected from the API key — do NOT send merchant_id.', fields: BUCKET_FIELDS, body: { total_boxes: 3, freight_collection_type: 1, pickup_type: 1, insurance_type: 1, is_fragile: 0, dimension_data: [{ qty: '3', length_in_cms: '27', breadth_in_cms: '30', height_in_cms: '44' }], invoice_data: [{ product_invoice_value: '0.00', invoice_no: 'SIK-26-000694', eway_bill_no: '', order_no: 'SO/26-01052' }], suborders_data: [{ ident: '1', count: '56.00', description: 'BOOKS' }, { ident: '2', count: '56.00', description: 'BOOKS' }, { ident: '3', count: '58.00', description: 'BOOKS' }], product_weight_in_kgs: 1.83, customer_mobile: '9848778938', customer_alt_no: '9848778938', customer_name: 'Kips - Hanumanthu Rushikesh Dora', customer_address: 'Plot 12 Industrial Area', customer_city: 'Guntur', customer_state: 'A.P', customer_pincode: 522007, customer_email: '' } },
    { method: 'POST', path: '/v1/api/v1/orders/bulk', desc: 'Bulk create orders (up to 250 per call)', body: { orderType: 'B2C', orders: [{ orderNo: 'ORD-1001', customerName: '...', customerMobile: '...', customerPincode: '560001', pickupPincode: '201301', codValue: 1499 }, { orderNo: 'ORD-1002', customerName: '...', customerMobile: '...', customerPincode: '110001', pickupPincode: '201301', codValue: 0 }] } },
    { method: 'GET',  path: '/v1/api/v1/track/{awb}', desc: 'Pull current tracking + event history for an AWB / LR / orderNo' },
    { method: 'POST', path: '/v1/api/v1/price-calculator/b2c', desc: 'What a B2C shipment would cost, courier by courier, before you book it \u2014 forward + fuel + COD + GST, with TAT and the cheapest/fastest flags. Priced off YOUR rate card; the merchant comes from the API key, so do not send merchantId.', body: { fromPincode: '201301', toPincode: '560001', weight: 0.8, length: 25, breadth: 15, height: 5, invoiceValue: 1499, deliveryType: 'COD', codValue: 1499 } },
    { method: 'POST', path: '/v1/api/v1/price-calculator/b2b', desc: 'The same quote for a B2B consignment \u2014 priced per kg off your zone / state / city matrix, with the min-LR charge and the other charges your plan carries.', body: { fromPincode: '201301', toPincode: '560066', weight: 50, length: 100, breadth: 60, height: 60, totalBoxes: 5, invoiceValue: 250000, deliveryType: 'PREPAID' } },
    { method: 'GET',  path: '/v1/api/v1/serviceability?from=201301&to=560001', desc: 'Lane serviceability across your assigned couriers (TAT + COD allowed + zone)' },
    { method: 'GET',  path: '/v1/api/v1/ping', desc: 'Sanity check — returns your merchantId from the API key' },
  ];
  return (
    <div className="space-y-4">
      <div className="bg-slate-900 text-slate-100 rounded-lg p-4">
        <div className="text-xs text-emerald-400 mb-1">Base URL</div>
        <code className="font-mono">{base}</code>
        <div className="text-xs text-emerald-400 mt-3 mb-1">Authentication</div>
        <div className="text-xs">Add header: <code className="bg-slate-800 px-1.5 py-0.5 rounded">X-Api-Key: gnt_live_xxxxxxxx…</code> (or <code className="bg-slate-800 px-1.5 py-0.5 rounded">Authorization: Bearer gnt_live_xxxxxxxx…</code>)</div>
      </div>
      {endpoints.map((e) => (
        <details key={e.path} className="border rounded bg-white">
          <summary className="cursor-pointer px-4 py-3 hover:bg-slate-50 flex items-center gap-3">
            <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${e.method === 'GET' ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700'}`}>{e.method}</span>
            <code className="font-mono text-sm">{e.path}</code>
            <span className="text-xs text-slate-500 flex-1">{e.desc}</span>
          </summary>
          <div className="px-4 py-3 border-t bg-slate-50">
            <div className="text-xs font-semibold text-slate-700 mb-1">Request</div>
            <pre className="bg-slate-900 text-emerald-300 text-xs p-3 rounded overflow-x-auto"><code>{`curl -X ${e.method} '${base}${e.path}' \\
  -H 'X-Api-Key: gnt_live_YOUR_KEY' ${e.body ? `\\
  -H 'Content-Type: application/json' \\
  -d '${JSON.stringify(e.body, null, 2)}'` : ''}`}</code></pre>
            {'fields' in e && Array.isArray((e as any).fields) && <>
              <div className="text-xs font-semibold text-slate-700 mt-3 mb-1">Fields <span className="font-normal text-slate-500">— all optional</span></div>
              <div className="overflow-x-auto border border-slate-200 rounded bg-white">
                <table className="w-full text-xs">
                  <thead className="bg-slate-100 text-slate-600"><tr>
                    <th className="text-left px-2 py-1.5">Field</th><th className="text-left px-2 py-1.5">Type</th><th className="text-left px-2 py-1.5">Required</th><th className="text-left px-2 py-1.5">Meaning</th>
                  </tr></thead>
                  <tbody>
                    {((e as any).fields as DocField[]).map((f) => (
                      <tr key={f.name} className="border-t border-slate-100 align-top">
                        <td className="px-2 py-1.5 font-mono whitespace-nowrap">{f.name}</td>
                        <td className="px-2 py-1.5 text-slate-500">{f.type}</td>
                        <td className="px-2 py-1.5">{f.required ? 'Yes' : 'No'}</td>
                        <td className="px-2 py-1.5 text-slate-700">{f.desc}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>}
            {e.body && <>
              <div className="text-xs font-semibold text-slate-700 mt-3 mb-1">Body example</div>
              <pre className="bg-slate-900 text-emerald-300 text-xs p-3 rounded overflow-x-auto"><code>{JSON.stringify(e.body, null, 2)}</code></pre>
            </>}
          </div>
        </details>
      ))}
      <div className="bg-emerald-50 border border-emerald-200 rounded p-4 text-sm space-y-2">
        <div className="font-semibold text-emerald-900">Booking response</div>
        <p className="text-xs text-emerald-900">Creating an order also books it. The order is returned as before, plus a <code>booking</code> block:</p>
        <pre className="bg-slate-900 text-emerald-300 text-xs p-3 rounded overflow-x-auto"><code>{`"booking": {
  "ok": true,
  "awbNo": "800000010759",
  "lrNo": null,
  "courierId": 5,
  "courierName": "Gaint 250Gm Surface",
  "status": "Booking Received",
  "message": "Booked"
}`}</code></pre>
        <ul className="text-xs text-emerald-900 list-disc pl-5 space-y-1">
          <li><b>Courier:</b> send <code>courierId</code> to choose one of your enabled couriers; leave it out and your highest-priority courier that serves the delivery pincode is used.</li>
          <li><b>Pickup location:</b> send <code>warehouseId</code>, or a <code>pickupPincode</code> that matches one of your pickup locations. With a single location nothing is needed.</li>
          <li><b>Not booked?</b> <code>booking.ok</code> is false and <code>booking.message</code> says why. The order is still saved and our team can ship it.</li>
          <li><b>Retries are safe:</b> sending the same <code>orderNo</code> again returns the existing order (<code>booking.duplicate: true</code>) and never books a second shipment. If the first attempt was not booked, the retry books it.</li>
        </ul>
      </div>
      <div className="bg-blue-50 border border-blue-200 rounded p-4 text-sm space-y-2">
        <div className="font-semibold text-blue-900 flex items-center gap-2"><Webhook size={14} /> Webhook events (we → you)</div>
        <p className="text-xs text-blue-800">Register an endpoint under the <b>Webhooks</b> tab and we call it as each scan happens — no polling needed. You can send a test call and inspect every delivery there.</p>
        <pre className="bg-slate-900 text-emerald-300 text-xs p-3 rounded overflow-x-auto"><code>{`POST your_webhook_url
Content-Type: application/json
X-Gaint-Event: tracking.updated

{
  "event": "tracking.updated",
  "awbNo": "GNTB2B124012",
  "lrNo": "GL110030646",
  "orderNo": "SO-4471",
  "orderType": "B2B",
  "status": { "code": 8, "name": "Delivered" },
  "scan": { "time": "…", "location": "Gurugram, Haryana", "remark": "Delivered to consignee" },
  "courier": "Gaint 10Kg Courier",
  "consignee": { "name": "…", "mobile": "…", "city": "…", "pincode": "122015" }
}`}</code></pre>
        <p className="text-xs text-blue-800">Prefer <b>GET</b>? Choose it on the webhook and the same fields arrive as query-string parameters. The <b>Webhooks</b> tab shows a live preview of both.</p>
      </div>
    </div>
  );
}

